import { useEffect, useState } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

export interface GraphicUploadProps {
    label: string;
    /** Backdrop the preview is shown against, so the logo can be evaluated in context. */
    backdrop?: "light" | "dark" | "neutral";
    /** URL of the graphic currently stored on the server, if we can display it (null otherwise). */
    currentUrl?: string | null;
    /** Whether a graphic already exists on the server (drives the "Uploaded" status even without a preview URL). */
    uploaded?: boolean;
    /** A newly picked (not-yet-uploaded) file. */
    file?: File | null;
    /** Whether the stored graphic is marked for removal on the next save. */
    cleared?: boolean;
    onSelect: (file: File) => void;
    /** Mark the stored graphic for removal (shown only when one exists). */
    onClear?: () => void;
    /** Undo a pending removal. */
    onRestore?: () => void;
}

const GraphicUpload = ({ label, backdrop = "neutral", currentUrl, uploaded, file, cleared, onSelect, onClear, onRestore }: GraphicUploadProps) => {
    const [filePreview, setFilePreview] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Build (and clean up) an object URL for the locally picked file
    useEffect(() => {
        if (!file) {
            setFilePreview(null);
            return;
        }
        const url = URL.createObjectURL(file);
        setFilePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    // A pending removal hides the stored preview; a freshly picked file always previews.
    const preview = filePreview ?? (cleared ? null : currentUrl) ?? null;
    const hasStored = !!(uploaded || currentUrl);

    const backdropBg =
        backdrop === "dark" ? "#1E1E1E" : backdrop === "light" ? "#FFFFFF" : "background.default";

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        e.target.value = ""; // reset so re-picking the same file still fires onChange
        if (!f) return;
        // `accept` is only a hint, so validate the type (and extension as a fallback) explicitly.
        if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) {
            setError("PNG files only");
            return;
        }
        setError(null);
        onSelect(f);
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, width: 260, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="subtitle2">{label}</Typography>

            <Box
                sx={{
                    height: 150,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: backdropBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    p: 1,
                }}
            >
                {preview ? (
                    <Box
                        component="img"
                        src={preview}
                        alt={label}
                        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                ) : (
                    <Typography variant="caption" color={backdrop === "dark" ? "grey.500" : "text.secondary"}>
                        No graphic uploaded
                    </Typography>
                )}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Chip
                    size="small"
                    label={file ? "Pending upload" : cleared ? "Removing" : hasStored ? "Uploaded" : "None"}
                    color={file ? "warning" : cleared ? "error" : hasStored ? "success" : "default"}
                    variant={file || cleared || hasStored ? "filled" : "outlined"}
                />
                <Stack direction="row" spacing={0.5}>
                    {/* Remove/Undo only applies to a stored graphic and hides while a replacement file is pending. */}
                    {hasStored && !file && (cleared
                        ? <Button size="small" color="inherit" onClick={onRestore}>Undo</Button>
                        : onClear && <Button size="small" color="error" onClick={onClear}>Remove</Button>)}
                    <Button size="small" component="label" startIcon={<CloudUploadIcon />}>
                        {file || (hasStored && !cleared) ? "Replace" : "Upload"}
                        <input type="file" hidden accept="image/png,.png" onChange={handleChange} />
                    </Button>
                </Stack>
            </Stack>

            {error ? (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            ) : (
                file && (
                    <Typography variant="caption" color="text.secondary" noWrap title={file.name}>
                        {file.name}
                    </Typography>
                )
            )}
        </Paper>
    );
};

export default GraphicUpload;
